//* Libraries imports
import { useEffect, useState } from "react";
//* Types imports
import type { MappingRule } from "@/features/template/types";

type FieldItem = { id: string; name: string };

interface TemplateFieldsListProps {
  tags: string[];
  selectedTag: string | null;
  onTagSelect: (tag: string) => void;
  replacements: Record<string, string>;
  mappings?: Record<string, MappingRule>;
}

export const TemplateFieldsList = ({
  tags,
  selectedTag,
  onTagSelect,
  replacements,
  mappings = {},
}: TemplateFieldsListProps) => {
  const [fields, setFields] = useState<FieldItem[]>([]);

  useEffect(() => {
    const mapped = tags.map((t, i) => ({
      id: String(i + 1).padStart(2, "0"),
      name: t,
    }));
    setFields(mapped);
  }, [tags]);

  return (
    <section className="overflow-hidden z-0 w-full rounded-xl bg-zinc-50 flex flex-col h-full max-md:max-w-full">
      <hr className="my-4 border-slate-200" />
      <h3 className="flex items-start px-3 w-full text-xl font-medium text-center text-zinc-800 max-md:max-w-full">
        Campos Identificados
      </h3>
      <hr className="my-5 border-slate-200" />

      <div className="flex flex-col flex-1 overflow-y-auto">
        {fields.map((field, index) => {
          const isSelected = selectedTag === field.name;
          const isFilled =
            (replacements[field.name] &&
              replacements[field.name].trim() !== "") ||
            (mappings[field.name] && mappings[field.name].type !== "fixed");

          return (
            <div
              key={field.id}
              className="flex flex-col w-full cursor-pointer"
              onClick={() => onTagSelect(field.name)}
            >
              <div
                className={`flex overflow-hidden justify-center items-center py-4 w-full px-4 transition-colors ${
                  isSelected ? "bg-sky-50" : "hover:bg-gray-50"
                }`}
              >
                <div className="flex overflow-hidden flex-wrap flex-1 shrink gap-2.5 items-center self-stretch my-auto w-full basis-0 min-w-0 max-md:max-w-full">
                  <div className="flex gap-2.5 justify-center items-center self-stretch my-auto text-base font-medium text-center whitespace-nowrap text-zinc-400">
                    <span
                      className={`self-stretch my-auto ${
                        isSelected
                          ? "text-sky-600 font-bold"
                          : isFilled
                          ? "text-emerald-600 font-semibold"
                          : "text-zinc-400"
                      } w-[21px]`}
                    >
                      {field.id}
                    </span>
                    <div
                      className={`shrink-0 self-stretch my-auto size-5 border border-solid ${
                        isFilled
                          ? "bg-emerald-200 border-emerald-200"
                          : "bg-slate-200 border-slate-200"
                      }`}
                    />
                  </div>
                  <span
                    title={field.name}
                    className={`flex-1 shrink self-stretch my-auto text-base truncate ${
                      isSelected
                        ? "text-sky-600 font-semibold"
                        : isFilled
                        ? "text-emerald-700"
                        : "text-gray-700"
                    } basis-0 min-w-0 max-md:max-w-full`}
                  >
                    {field.name}
                  </span>
                  <button className="flex gap-4 items-center self-stretch w-5 h-full shrink-0">
                    <div
                      className={`flex flex-col justify-center items-center self-stretch px-1.5 my-auto size-5 rounded-xl aspect-[1/1] min-h-5 ${
                        isSelected
                          ? "bg-sky-600"
                          : isFilled
                          ? "bg-emerald-500"
                          : "bg-slate-500"
                      }`}
                    >
                      {isFilled && !isSelected ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="size-3 text-white"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      ) : (
                        <img
                          src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/82b274e4e67d427942c9df2442b4e8294ada4f44?placeholderIfAbsent=true"
                          alt="Select"
                          className="object-contain w-full aspect-[1.67] stroke-[2px] stroke-zinc-50"
                        />
                      )}
                    </div>
                  </button>
                </div>
              </div>
              {index < fields.length - 1 && (
                <hr className="my-0 border-slate-200" />
              )}
            </div>
          );
        })}
      </div>

      {fields.length === 0 && (
        <div className="flex justify-center items-center p-10 text-gray-400">
          Nenhum campo identificado.
        </div>
      )}

      <div className="flex absolute top-5 z-0 flex-col pb-56 h-[530px] right-[11px] w-[5px] max-md:pb-24">
        <img
          src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/7e37071d7be11060967ba236cc4fdf04e639f2e1?placeholderIfAbsent=true"
          alt="Scroll"
          className="object-contain mb-0 w-full aspect-[0.02] fill-slate-300 max-md:mb-2.5"
        />
      </div>
    </section>
  );
};
